import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSEO, breadcrumb } from "@/lib/seo";
import { useI18n } from "@/i18n";
import { FAQ } from "@/components/FAQ";
import DevisForm from "@/components/DevisForm";
import { SectionTitle, DataTable, MatrixTable, LabelledList, EventPageHero } from "@/components/PageBlocks";
import { ArrowRight, Check } from "lucide-react";
import { SEMINAIRE_CONTENT } from "@/content/seminaire";
import { usePageContent } from "@/lib/pageContent";
import { pageImageUrl } from "@/content/pageImages";
import { Rich } from "@/components/Rich";

const SITE = "https://lentracterestaurant.fr";

const SEMINAR_SERVICE_LD = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${SITE}/seminaire#service`,
  "name": "Séminaire d'entreprise à L'Entracte",
  "serviceType": "Location de salle de séminaire avec restauration",
  "description":
    "Journées et demi-journées d'étude dans un hôtel particulier à Ville-d'Avray : salle privatisée, Wi-Fi, écran, pauses et déjeuner du chef inclus.",
  "provider": { "@id": `${SITE}/#lieu` },
  "areaServed": [
    { "@type": "City", "name": "Ville-d'Avray" },
    { "@type": "Place", "name": "La Défense" },
    { "@type": "City", "name": "Paris" },
    { "@type": "City", "name": "Versailles" },
    { "@type": "City", "name": "Boulogne-Billancourt" },
    { "@type": "City", "name": "Saint-Cloud" },
  ],
  "offers": [
    {
      "@type": "Offer",
      "name": "Demi-journée d'étude",
      "url": `${SITE}/seminaire`,
      "priceCurrency": "EUR",
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "price": 90,
        "priceCurrency": "EUR",
        "valueAddedTaxIncluded": false,
        "unitText": "par personne",
      },
    },
    {
      "@type": "Offer",
      "name": "Journée d'étude",
      "url": `${SITE}/seminaire`,
      "priceCurrency": "EUR",
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "price": 110,
        "priceCurrency": "EUR",
        "valueAddedTaxIncluded": false,
        "unitText": "par personne",
      },
    },
  ],
};

export default function Seminaire() {
  const { lang, localePath, alternateUrls } = useI18n();
  const c = usePageContent("seminaire", lang === "en" ? "en" : "fr", SEMINAIRE_CONTENT[lang === "en" ? "en" : "fr"]);
  const [imgs, setImgs] = useState({});

  useSEO({
    title: c.meta.title,
    description: c.meta.description,
    keywords: c.meta.keywords,
    lang,
    alternates: alternateUrls(),
    jsonLd: [
      breadcrumb([
        { name: lang === "en" ? "Home" : "Accueil", url: `${SITE}/` },
        { name: lang === "en" ? "Events" : "Événements", url: `${SITE}/evenements` },
        { name: lang === "en" ? "Seminar" : "Séminaire", url: `${SITE}/seminaire` },
      ]),
      SEMINAR_SERVICE_LD,
    ],
  });

  useEffect(() => {
    api.get("/settings/public").then((r) => setImgs(r.data || {})).catch(() => {});
  }, []);

  const HERO = pageImageUrl(imgs, "seminaire", "hero");
  const LUNCH = pageImageUrl(imgs, "seminaire", "lunch");
  const BREAK_IMG = pageImageUrl(imgs, "seminaire", "break");

  const crumbs = c.breadcrumb.map((b) => ({ label: b.label, to: b.to ? localePath(b.to) : undefined }));

  return (
    <div data-testid="seminaire-page">
      <EventPageHero
        overline={c.overline}
        h1={c.h1}
        intro={c.intro}
        ctaDevis={c.ctaDevis}
        ctaCall={c.ctaCall}
        image={HERO}
        alt={c.heroAlt}
        crumbs={crumbs}
        phoneSource="seminaire_hero"
      />

      {/* EN BREF */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#F6F0E7]">
        <div className="max-w-[1100px] mx-auto">
          <SectionTitle>{c.brief.title}</SectionTitle>
          <div className="mt-10">
            <DataTable caption={c.brief.caption} rows={c.brief.rows} testid="seminaire-brief-table" />
          </div>
        </div>
      </section>

      {/* FORMULES */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#FFFFFF]">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
          <div className="md:col-span-8">
            <SectionTitle>{c.formulas.title}</SectionTitle>
            <div className="mt-10">
              <MatrixTable
                caption={c.formulas.caption}
                head={c.formulas.head}
                rows={c.formulas.rows}
                testid="seminaire-formulas-table"
              />
            </div>
            <Rich as="p" value={c.formulas.note} className="mt-8 text-[#697585] leading-relaxed" />
          </div>
          <div className="md:col-span-4">
            <div className="aspect-[3/4] overflow-hidden">
              {LUNCH && <img src={LUNCH} alt={c.formulas.alt} className="w-full h-full object-cover img-soft" />}
            </div>
          </div>
        </div>
      </section>

      {/* AU-DELÀ */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#F6F0E7]">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="md:col-span-7">
            <SectionTitle>{c.beyond.title}</SectionTitle>
            <div className="mt-10">
              <LabelledList items={c.beyond.items} testid="seminaire-beyond" />
            </div>
            <p className="mt-6 text-[#3D4A5A] leading-relaxed">
              {c.beyond.groupsIntro}{" "}
              <Link
                to={localePath(c.beyond.groupsLink)}
                data-testid="seminaire-groups-link"
                className="text-[#B8956A] underline underline-offset-4 hover:text-[#203240] transition-colors"
              >
                {c.beyond.groupsLinkLabel}
              </Link>
              .
            </p>
          </div>
          <div className="md:col-span-5">
            <div className="aspect-[4/3] overflow-hidden">
              {BREAK_IMG && (
                <img src={BREAK_IMG} alt={c.beyond.alt} className="w-full h-full object-cover img-soft" />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* POURQUOI */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#FFFFFF]">
        <div className="max-w-[1000px] mx-auto">
          <SectionTitle>{c.why.title}</SectionTitle>
          <div className="mt-10">
            <LabelledList items={c.why.items} testid="seminaire-why" />
          </div>
        </div>
      </section>

      {/* ACCÈS */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#F6F0E7]">
        <div className="max-w-[1000px] mx-auto">
          <SectionTitle>{c.access.title}</SectionTitle>
          <Rich as="p" value={c.access.intro} className="mt-8 text-[#3D4A5A] leading-relaxed" />
          <ul className="mt-8 space-y-5">
            {c.access.items.map((it) => (
              <li key={it} className="flex items-start gap-3 text-[#3D4A5A] leading-relaxed">
                <Check size={18} className="text-[#B8956A] mt-1 shrink-0" />
                <Rich value={it} />
              </li>
            ))}
          </ul>
          <Rich as="p" value={c.access.note} className="mt-8 text-[#697585] leading-relaxed" />
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#FFFFFF]">
        <div className="max-w-[1000px] mx-auto">
          <SectionTitle>{c.faqTitle}</SectionTitle>
          <div className="mt-10">
            <FAQ items={c.faq} testid="seminaire-faq" schemaId="ld-faq-seminaire" />
          </div>
        </div>
      </section>

      {/* DEVIS */}
      <section id="devis" className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#F6F0E7] scroll-mt-32">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
          <div className="md:col-span-5">
            <p className="overline">{c.overline}</p>
            <SectionTitle>{c.devis.title}</SectionTitle>
            <Rich as="p" value={c.devis.body} className="mt-8 text-[#3D4A5A] leading-relaxed" />
            <p className="mt-6 text-[#697585]">{c.devis.contact}</p>
          </div>
          <div className="md:col-span-7">
            <div className="border border-[#D9D3C5] bg-[#FFFFFF] p-7 md:p-10">
              <DevisForm defaultEventType={c.eventType} pageSource="/seminaire" />
            </div>
          </div>
        </div>
      </section>

      {/* LIENS INTERNES */}
      <section className="py-16 px-6 md:px-12 lg:px-16 bg-[#FFFFFF] border-t border-[#D9D3C5]">
        <div className="max-w-[1500px] mx-auto">
          <p className="overline">{c.related.title}</p>
          <div className="mt-6 flex flex-wrap gap-x-10 gap-y-4">
            {c.related.links.map((l) => (
              <Link
                key={l.to}
                to={localePath(l.to)}
                className="inline-flex items-center gap-2 text-[#203240] hover:text-[#B8956A] transition-colors"
              >
                {l.label} <ArrowRight size={14} className="text-[#B8956A]" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
